/**
 * Helpers for the label-raster brush/eraser radius controls.
 *
 * The viewer allows radii up to a quarter of the slide width (often > 10k px), but
 * annotators almost always work in the 1–50 px range. A linear slider therefore
 * squeezes the useful range into a few pixels of travel. These helpers map the
 * radius onto a logarithmic slider so the low end gets most of the travel, and
 * provide single-pixel stepping for the +/− buttons.
 */

/** Number of discrete positions on the slider track. */
export const BRUSH_RADIUS_SLIDER_STEPS = 1000

export const MIN_BRUSH_RADIUS_PX = 1

/** Clamps `radiusPx` to the inclusive integer range `[1, maxRadiusPx]`. */
export function clampBrushRadius(radiusPx: number, maxRadiusPx: number): number {
  const max = Math.max(MIN_BRUSH_RADIUS_PX, Math.floor(maxRadiusPx))
  if (!Number.isFinite(radiusPx)) return MIN_BRUSH_RADIUS_PX
  return Math.min(max, Math.max(MIN_BRUSH_RADIUS_PX, Math.round(radiusPx)))
}

/** Returns `radiusPx + delta` clamped to `[1, maxRadiusPx]`. */
export function stepBrushRadius(radiusPx: number, delta: number, maxRadiusPx: number): number {
  return clampBrushRadius(clampBrushRadius(radiusPx, maxRadiusPx) + delta, maxRadiusPx)
}

/**
 * Maps a radius onto a slider position in `[0, BRUSH_RADIUS_SLIDER_STEPS]` using a
 * log scale, so that position `0` is radius 1 and the last position is `maxRadiusPx`.
 */
export function brushRadiusToSliderPosition(radiusPx: number, maxRadiusPx: number): number {
  const max = Math.max(MIN_BRUSH_RADIUS_PX, Math.floor(maxRadiusPx))
  if (max <= MIN_BRUSH_RADIUS_PX) return 0
  const r = clampBrushRadius(radiusPx, max)
  const t = Math.log(r / MIN_BRUSH_RADIUS_PX) / Math.log(max / MIN_BRUSH_RADIUS_PX)
  return Math.round(t * BRUSH_RADIUS_SLIDER_STEPS)
}

/** Inverse of {@link brushRadiusToSliderPosition}; always returns an integer radius in `[1, maxRadiusPx]`. */
export function sliderPositionToBrushRadius(position: number, maxRadiusPx: number): number {
  const max = Math.max(MIN_BRUSH_RADIUS_PX, Math.floor(maxRadiusPx))
  if (max <= MIN_BRUSH_RADIUS_PX) return MIN_BRUSH_RADIUS_PX
  const t = Math.min(1, Math.max(0, (Number.isFinite(position) ? position : 0) / BRUSH_RADIUS_SLIDER_STEPS))
  const r = MIN_BRUSH_RADIUS_PX * (max / MIN_BRUSH_RADIUS_PX) ** t
  return clampBrushRadius(r, max)
}
